import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

const DEMO_DEFAULT = import.meta.env.VITE_ENGINEERING_BRIDGE_DEMO === 'true';

interface EngineeringBridgeContextType {
    isDemo: boolean;
    toggleMode: () => void;
    dataSource: 'live' | 'mock';
    isLoading: boolean;
    setIsLoading: (loading: boolean) => void;
}

const EngineeringBridgeContext = createContext<EngineeringBridgeContextType | undefined>(undefined);

interface EngineeringBridgeProviderProps {
    children: ReactNode;
    initialIsDemo?: boolean;
}

export const EngineeringBridgeProvider: React.FC<EngineeringBridgeProviderProps> = ({
    children,
    initialIsDemo,
}) => {
    const [isDemo, setIsDemo] = useState(initialIsDemo ?? DEMO_DEFAULT);
    const [dataSource, setDataSource] = useState<'live' | 'mock'>('mock');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (initialIsDemo !== undefined) {
            setIsDemo(initialIsDemo);
        }
    }, [initialIsDemo]);

    useEffect(() => {
        setDataSource(isDemo ? 'mock' : 'live');
    }, [isDemo]);

    const toggleMode = () => {
        setIsDemo(!isDemo);
    };

    return (
        <EngineeringBridgeContext.Provider
            value={{ isDemo, toggleMode, dataSource, isLoading, setIsLoading }}
        >
            {children}
        </EngineeringBridgeContext.Provider>
    );
};

export const useEngineeringBridgeContext = (): EngineeringBridgeContextType => {
    const context = useContext(EngineeringBridgeContext);
    if (context === undefined) {
        throw new Error(
            'useEngineeringBridgeContext must be used within an EngineeringBridgeProvider'
        );
    }
    return context;
};

export { EngineeringBridgeContext };
